/**
 * Admin — Store Status controls.
 * Persisted at settings.commerce.store_status via
 * PATCH /store/settings/commerce (existing free-form endpoint).
 */
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Save, Power, CalendarDays, Timer, Plus, Trash2 } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MODES = [
  { v: "open",        label: "Open",        desc: "Store is open, accepting orders." },
  { v: "closed",      label: "Closed",      desc: "Store is closed. Customers can browse only." },
  { v: "holiday",     label: "Holiday",     desc: "Closed today as a public/private holiday." },
  { v: "maintenance", label: "Maintenance", desc: "Temporarily paused for maintenance." },
];

const empty = () => ({
  mode: "open",
  accept_orders: true,
  opening_time: "09:00",
  closing_time: "22:00",
  weekly_off: [],
  holidays: [],
  temp_closure_start: "",
  temp_closure_end: "",
  custom_message: "",
});

export default function StoreStatus() {
  const [commerceAll, setCommerceAll] = useState({});
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/settings");
      const c = data?.settings?.commerce || {};
      setCommerceAll(c);
      const s = c.store_status || {};
      setForm({ ...empty(), ...s, weekly_off: s.weekly_off || [], holidays: s.holidays || [] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/store/settings/commerce", { ...commerceAll, store_status: form });
      toast.success("Store status updated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      weekly_off: f.weekly_off.includes(d) ? f.weekly_off.filter((x) => x !== d) : [...f.weekly_off, d],
    }));
  };

  const addHoliday = () => setForm((f) => ({ ...f, holidays: [...f.holidays, { date: "", label: "" }] }));
  const rmHoliday = (i) => setForm((f) => ({ ...f, holidays: f.holidays.filter((_, x) => x !== i) }));

  const badge = form.mode === "open" && form.accept_orders
    ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Store open</span>
    : <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-3 py-1 text-xs font-semibold"><span className="w-2 h-2 rounded-full bg-destructive" /> Store closed</span>;

  return (
    <div className="p-8 space-y-6" data-testid="store-status-page">
      <PageHeader
        title="Store Status"
        subtitle="Control store operations, opening hours, holidays and temporary closures."
        actions={
          <div className="flex items-center gap-3">
            {badge}
            <Button onClick={save} disabled={saving || loading} className="btn-primary-glow" data-testid="ss-save">
              <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Status */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Power className="w-4 h-4 text-primary" /> Operating status</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Mode</Label>
              <RadioGroup value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })} className="grid grid-cols-2 gap-2">
                {MODES.map((m) => (
                  <label key={m.v} className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition ${form.mode === m.v ? "border-primary bg-primary/5" : "hover:bg-secondary/40"}`} data-testid={`ss-mode-${m.v}`}>
                    <RadioGroupItem value={m.v} id={`mode-${m.v}`} className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{m.label}</div>
                      <div className="text-[11px] text-muted-foreground leading-snug">{m.desc}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Accept new orders</div>
                <div className="text-xs text-muted-foreground">Turn off to freeze all incoming orders without changing the mode.</div>
              </div>
              <Switch checked={form.accept_orders} onCheckedChange={(v) => setForm({ ...form, accept_orders: v })} data-testid="ss-accept-toggle" />
            </div>
            <div>
              <Label>Custom customer message</Label>
              <Textarea
                rows={2}
                placeholder='e.g. "Orders will resume at 8 AM."'
                value={form.custom_message}
                onChange={(e) => setForm({ ...form, custom_message: e.target.value })}
                data-testid="ss-custom-msg"
              />
              <div className="text-[11px] text-muted-foreground mt-1">Shown on the storefront announcement bar when the store is not open.</div>
            </div>
          </CardContent>
        </Card>

        {/* Hours */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Timer className="w-4 h-4 text-primary" /> Hours &amp; weekly off</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Opening time</Label>
                <Input type="time" value={form.opening_time} onChange={(e) => setForm({ ...form, opening_time: e.target.value })} data-testid="ss-open-time" />
              </div>
              <div>
                <Label>Closing time</Label>
                <Input type="time" value={form.closing_time} onChange={(e) => setForm({ ...form, closing_time: e.target.value })} data-testid="ss-close-time" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Weekly off</Label>
              <div className="grid grid-cols-7 gap-1.5">
                {DAYS.map((d) => (
                  <button
                    key={d}
                    onClick={() => toggleDay(d)}
                    className={`rounded-lg border py-2 text-xs font-semibold transition ${form.weekly_off.includes(d) ? "border-primary bg-primary text-primary-foreground" : "hover:bg-secondary/60"}`}
                    data-testid={`ss-day-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">Marked days are closed automatically.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Temp. closure start</Label>
                <Input type="date" value={form.temp_closure_start} onChange={(e) => setForm({ ...form, temp_closure_start: e.target.value })} data-testid="ss-tc-start" />
              </div>
              <div>
                <Label>Temp. closure end</Label>
                <Input type="date" value={form.temp_closure_end} onChange={(e) => setForm({ ...form, temp_closure_end: e.target.value })} data-testid="ss-tc-end" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Holidays */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-primary" /> Holiday calendar</CardTitle>
              <Button size="sm" variant="outline" onClick={addHoliday} data-testid="ss-add-holiday"><Plus className="w-4 h-4 mr-1" />Add date</Button>
            </div>
          </CardHeader>
          <CardContent>
            {form.holidays.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">No holidays configured yet.</div>
            ) : (
              <div className="divide-y">
                {form.holidays.map((h, i) => (
                  <div key={i} className="grid grid-cols-[160px_1fr_auto] gap-3 items-center py-2" data-testid={`ss-holiday-${i}`}>
                    <Input type="date" value={h.date || ""} onChange={(e) => { const next = [...form.holidays]; next[i] = { ...h, date: e.target.value }; setForm({ ...form, holidays: next }); }} />
                    <Input placeholder="Holiday name" value={h.label || ""} onChange={(e) => { const next = [...form.holidays]; next[i] = { ...h, label: e.target.value }; setForm({ ...form, holidays: next }); }} />
                    <Button size="icon" variant="ghost" onClick={() => rmHoliday(i)} data-testid={`ss-holiday-rm-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
